/* eslint-disable react/display-name */
import React from 'react';
import { StrikethroughText, CodeLine, CodeBlock, Blockquote } from '../../../Typography';
import ImageViewer from '../../../Viewers/Skill/ImageViewer';
import { AttachmentViewer } from '../AttachmentViewer';
// just a helper to add a <br /> after a block
const addBreaklines = (children) => children.map((child, i) => [child, React.createElement("br", { key: i })]);
const buildComponentId = (key) => `component-${key}`;
const useRichTextRenderers = ({ VideoComponent, CodeComponent, LabComponent, QuestionComponent, LinkComponent, ImageComponent }) => {
    /**
     * Define the renderers
     */
    const renderers = {
        /**
         * Those callbacks will be called recursively to render a nested structure
         */
        inline: {
            // The key passed here is just an index based on rendering order inside a block
            BOLD: (children, { key }) => React.createElement("strong", { key: key }, children),
            ITALIC: (children, { key }) => React.createElement("em", { key: key }, children),
            UNDERLINE: (children, { key }) => React.createElement("u", { key: key }, children),
            STRIKETHROUGH: (children, { key }) => (React.createElement(StrikethroughText, { key: key }, children)),
            CODE: (children, { key }) => (React.createElement(CodeLine, { key: key }, children))
        },
        /**
         * Blocks receive children and depth
         * Note that children are an array of blocks with same styling,
         */
        blocks: {
            unstyled: (children, { keys }) => children.map((child, i) => (React.createElement("p", { key: i, id: buildComponentId(keys[i]) }, child))),
            blockquote: (children, { keys }) => (React.createElement(Blockquote, { id: buildComponentId(keys[0]) }, addBreaklines(children))),
            'header-one': (children, { keys }) => children.map((child, i) => (React.createElement(React.Fragment, null,
                React.createElement("h2", { id: buildComponentId(keys[i]), key: i }, child),
                React.createElement("br", null)))),
            'header-two': (children, { keys }) => children &&
                children.map((child, i) => (React.createElement("h2", { id: buildComponentId(keys[i]), key: i }, child))),
            // You can also access the original keys of the blocks
            'code-block': (children, { keys }) => (React.createElement(CodeBlock, { id: buildComponentId(keys[0]), key: keys[0] }, addBreaklines(children))),
            // or depth for nested lists
            'unordered-list-item': (children, { depth, keys }) => (React.createElement("ul", { id: buildComponentId(keys[0]), key: keys[keys.length - 1], className: `ul-level-${depth}` }, children.map((child, i) => (React.createElement("li", { key: i }, child))))),
            'ordered-list-item': (children, { depth, keys }) => (React.createElement("ol", { id: buildComponentId(keys[0]), key: keys.join('|'), className: `ol-level-${depth}` }, children === null || children === void 0 ? void 0 : children.map((child, i) => (React.createElement("li", { key: keys[i] }, child)))))
            // If your blocks use meta data it can also be accessed like keys
            // atomic: (children, { keys, data }) => children.map((child, i) => <Atomic key={keys[i]} {...data[i]} />),
        },
        /**
         * Entities receive children and the entity data
         */
        entities: {
            // key is the entity key value from raw
            LINK: (children, data, { key }) => {
                return LinkComponent ? (React.createElement(LinkComponent, Object.assign({ key: key }, data), children)) : (React.createElement("a", { key: key, href: data === null || data === void 0 ? void 0 : data.url, target: "_blank", rel: "noreferrer" }, children));
            },
            ATTACHMENT: (_children, data, { key }) => {
                return React.createElement(AttachmentViewer, { key: key, componentValues: data });
            },
            VIDEO: (_children, data, { key }) => {
                return VideoComponent ? React.createElement(VideoComponent, Object.assign({ key: key }, data)) : null;
            },
            IMAGE: (_children, data, { key }) => {
                return ImageComponent ? (React.createElement(ImageComponent, { key: key, componentValues: data })) : (React.createElement(ImageViewer, { key: key, componentValues: data }));
            },
            CODE: (_children, data, { key }) => {
                return CodeComponent ? React.createElement(CodeComponent, Object.assign({ key: key }, data)) : null;
            },
            LAB: (_children, data, { key }) => {
                return LabComponent ? React.createElement(LabComponent, Object.assign({ key: key }, data)) : null;
            },
            DIVIDER: () => React.createElement("hr", null),
            QUESTION: (_children, data, { key }) => {
                return QuestionComponent ? (React.createElement(QuestionComponent, Object.assign({ key: key }, data))) : null;
            }
        }
        /**
         * Array of decorators,
         * Entities receive children and the entity data,
         * inspired by https://facebook.github.io/draft-js/docs/advanced-topics-decorators.html
         * it's also possible to pass a custom Decorator class that matches the [DraftDecoratorType](https://github.com/facebook/draft-js/blob/master/src/model/decorators/DraftDecoratorType.js)
         */
        // decorators: [
        //     {
        // by default linkStrategy receives a ContentBlock stub (more info under Creating the ContentBlock)
        // strategy only receives first two arguments, contentState is yet not provided
        // strategy: linkStrategy,
        // component - a callback as with other renderers
        // decoratedText a plain string matched by the strategy
        // if your decorator depends on draft-js contentState you need to provide convertFromRaw in redraft options
        // component: ({ children, decoratedText }) => <a href={decoratedText}>{children}</a>,
        // },
        // new CustomDecorator(someOptions),
        // ],
    };
    return renderers;
};
export default useRichTextRenderers;
//# sourceMappingURL=useRichTextRenderers.js.map